import React, { cloneElement, isValidElement } from 'react';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';

export const RESTRICTED_TOOLTIP = 'Your role does not permit this action';

/**
 * Wraps an interactive child (Button, IconButton, etc.) so that when
 * `allowed` is false the child is rendered in a disabled state with a
 * tooltip explaining why.
 *
 * MUI's disabled buttons swallow hover events, so the disabled child is
 * wrapped in a span to keep the Tooltip working.
 */
export function RestrictedAction({
  allowed,
  tooltip = RESTRICTED_TOOLTIP,
  children,
  wrapperStyle,
}) {
  if (allowed) return children;
  if (!isValidElement(children)) return children;

  const disabledChild = cloneElement(children, {
    disabled: true,
    onClick: undefined,
    onSubmit: undefined,
  });

  return (
    <Tooltip title={tooltip} arrow>
      <span
        style={{
          display: 'inline-block',
          cursor: 'not-allowed',
          ...wrapperStyle,
        }}
      >
        {disabledChild}
      </span>
    </Tooltip>
  );
}

/**
 * Wraps a non-button region (e.g. a file drop zone) so that when
 * `allowed` is false the region is visually muted and non-interactive,
 * with a tooltip on hover.
 */
export function RestrictedRegion({
  allowed,
  tooltip = RESTRICTED_TOOLTIP,
  children,
}) {
  if (allowed) return children;

  return (
    <Tooltip title={tooltip} arrow>
      <Box
        sx={{
          position: 'relative',
          opacity: 0.5,
          pointerEvents: 'none',
          cursor: 'not-allowed',
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
}

export default RestrictedAction;
